import { useEffect, useMemo } from 'react';

import type { CoreStoreType } from '../../store/CoreStore';
import { useBetSlice } from '../../store/slices/bets/selectors';
import { useConfigSlice } from '../../store/slices/config/selectors';
import { useGameStateSlice } from '../../store/slices/game/selectors';
import { useSettingsSlice } from '../../store/slices/settings/selectors';

import { CrossOriginCommunicationController } from './CrossOriginCommunicationController';
import { type ICrossOriginCommunicator, IntegrationApi, OutgoingMessage } from './types';

export const crossOriginCommunicationController = new CrossOriginCommunicationController();

export interface CrossOriginCommunicationControllerConnectorProps {
  gameStore: CoreStoreType;
}

export const CrossOriginCommunicationControllerConnector = ({
  gameStore,
}: CrossOriginCommunicationControllerConnectorProps): null => {
  const casinoConfig = useConfigSlice.casinoConfig();

  useEffect(() => {
    if (gameStore && casinoConfig) {
      /**
       * if no configuration is coming from response
       * or it gets parsed to an empty array,
       * the default communicator needs to be initialized
       */
      (casinoConfig.integrationApi?.length ? casinoConfig.integrationApi : ['']).forEach((integration) => {
        const communicatorPromise = importCommunicator(integration);
        if (!communicatorPromise) {
          return;
        }
        communicatorPromise.then((communicator) => {
          initCommunicator(crossOriginCommunicationController, communicator, gameStore);
          crossOriginCommunicationController.dispatch(OutgoingMessage.GameLoadStart);
        });
      });
    }
    return () => {
      crossOriginCommunicationController.close();
    };
  }, [casinoConfig, gameStore]);

  return null;
};

//TODO: This really requires refactoring, not the best way of handling that at all, should be a middleware or something
export const CrossOriginCommunicationControllerMessengerConnected = (): null => {
  const totalBet = useBetSlice.totalBet();
  useEffect(() => {
    crossOriginCommunicationController.dispatch(OutgoingMessage.UpdateBets);
  }, [totalBet]);

  const balance = useGameStateSlice.balance();
  useEffect(() => {
    crossOriginCommunicationController.dispatch(OutgoingMessage.UpdateBalance);
  }, [balance]);

  const winResult = useGameStateSlice.winResult();
  useEffect(() => {
    crossOriginCommunicationController.dispatch(OutgoingMessage.UpdateWin);
  }, [winResult]);

  /**
   * need to send only on/off cases not any volume change
   */
  const masterVolume = useSettingsSlice.getMasterChannel().value;
  const masterVolumeChange = useMemo(() => masterVolume > 0, [masterVolume]);
  useEffect(() => {
    crossOriginCommunicationController.dispatch(OutgoingMessage.UpdateSettings);
  }, [masterVolumeChange]);

  return null;
};

// Utils
export const importCommunicator = (integrationApi: string): void | Promise<ICrossOriginCommunicator> => {
  switch (integrationApi) {
    case IntegrationApi.PaddyPower:
      return import('./CrossOriginCommunicationPaddyPower').then((module) => module.default);
    case IntegrationApi.DBOX:
      return import('./CrossOriginCommunicationDBOX').then((module) => module.default);
    case IntegrationApi.SkyVegas:
      return import('./CrossOriginCommunicationSkyVegas').then((module) => module.default);
    case IntegrationApi.Default:
    case '':
      return import('./CrossOriginCommunicationDefault').then((module) => module.default);
    default:
      return;
  }
};

const initCommunicator = (
  controller: CrossOriginCommunicationController,
  communicator: ICrossOriginCommunicator,
  store: CoreStoreType
): void => {
  controller.setCommunicator(communicator);
  controller.setStore(store);
  controller.connect();
};
